import React from 'react';
import styled from 'styled-components';
import Button from '../common/Button';
import Input from '../common/Input';

const FullScreen = styled.div`
  position: fixed;
  z-index: 30;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(2px);
`;

const AskModalBlock = styled.div`
  width: 420px;
  background: #fff;
  padding: 2rem;
  border-radius: 12px;
  max-height: 95%;
  overflow: auto;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  animation: slideIn 0.2s ease-out;
  
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  h2 {
    margin: 0 0 1.5rem 0;
    font-size: 1.5rem;
    font-weight: 600;
    color: #212529;
  }
  
  .buttons {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    margin-top: 1.5rem;
  }
`;

const StyledButton = styled(Button)`
  height: 2.5rem;
  padding: 0 1.5rem;
  border-radius: 6px;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ItemBlock = styled.div`
  margin-bottom: 1rem;
`;

const NumberInputGroup = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 0.75rem;
  align-items: end;
  margin-bottom: 1rem;
  
  .divider {
    font-size: 1.5rem;
    font-weight: 600;
    color: #adb5bd;
    padding-bottom: 0.5rem;
    text-align: center;
  }
`;

const CreateForm = styled.form`
  margin: 0;
`;

const PreviewInfo = styled.div`
  background: #f8f9fa;
  padding: 1.25rem;
  margin-top: 1.5rem;
  border-radius: 8px;
  border: 2px solid #e9ecef;
  
  .preview-label {
    font-size: 0.875rem;
    color: #6c757d;
    margin-bottom: 0.75rem;
    font-weight: 500;
    text-align: center;
  }
  
  .preview-content {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    font-size: 1.25rem;
    color: #212529;
    font-weight: 600;
  }
  
  .number-badge {
    background: #fff;
    padding: 0.625rem 1.25rem;
    border-radius: 6px;
    border: 2px solid #4a90e2;
    color: #4a90e2;
    min-width: 60px;
    text-align: center;
  }
  
  .arrow {
    font-size: 1.5rem;
    color: #adb5bd;
  }
  
  .count-info {
    margin-top: 1rem;
    text-align: center;
    font-size: 0.95rem;
    color: #495057;
    padding-top: 1rem;
    border-top: 1px solid #dee2e6;
  }
`;

const GuideMessage = styled.div`
  background: #fff3cd;
  border: 1px solid #ffecb5;
  padding: 1rem;
  margin-top: 1.5rem;
  border-radius: 8px;
  text-align: center;
  color: #856404;
  font-size: 0.9rem;
  
  .icon {
    font-size: 1.2rem;
    margin-bottom: 0.25rem;
  }
`;

const LockerModal = ({
  visible,
  title,
  confirmText,
  cancelText = '취소',
  onConfirm,
  onCancel,
  form,
  onChangeField,
  isModify,
}) => {
  if (!visible) return null;
  
  // 락커 범위 계산
  const startNumber = parseInt(form.startSN) || 0;
  const endNumber = parseInt(form.endSN) || 0;
  const calculatedCount = endNumber >= startNumber ? endNumber - startNumber + 1 : 0;
  const hasNumbers = startNumber > 0 && endNumber > 0;
  const showPreview = hasNumbers && calculatedCount > 0;
  const showGuide = !hasNumbers;
  
  return (
    <FullScreen onClick={onCancel}>
      <AskModalBlock onClick={(e) => e.stopPropagation()}>
        <h2>{title}</h2>
        <CreateForm>
          <ItemBlock>
            <Input
              name="name"
              onChangeField={onChangeField}
              type="text"
              placeholder="예: 남성용 락커"
              label="락커 이름"
              value={form.name}
              oneLine
            />
          </ItemBlock>
          <NumberInputGroup>
            <Input
              name="startSN"
              onChangeField={onChangeField}
              type="text"
              placeholder="예: 250"
              label="시작 번호"
              value={form.startSN}
              oneLine
            />
            <div className="divider">~</div>
            <Input
              name="endSN"
              onChangeField={onChangeField}
              type="text"
              placeholder="예: 279"
              label="끝 번호"
              value={form.endSN}
              oneLine
            />
          </NumberInputGroup>
        </CreateForm>
        
        {showPreview && (
          <PreviewInfo>
            <div className="preview-label">{isModify ? '수정될 락커 범위' : '생성될 락커 범위'}</div>
            <div className="preview-content">
              <span className="number-badge">{startNumber}</span>
              <span className="arrow">→</span>
              <span className="number-badge">{endNumber}</span>
            </div>
            <div className="count-info">
              총 {calculatedCount}개의 락커가 {isModify ? '수정됩니다' : '생성됩니다'}
            </div>
          </PreviewInfo>
        )}
        
        {showGuide && (
          <GuideMessage>
            <div className="icon">💡</div>
            <div>시작 번호와 끝 번호를 입력해주세요</div>
          </GuideMessage>
        )}
        
        <div className="buttons">
          <StyledButton onClick={onCancel}>{cancelText}</StyledButton>
          <StyledButton onClick={onConfirm}>{confirmText}</StyledButton>
        </div>
      </AskModalBlock>
    </FullScreen>
  );
};

export default React.memo(LockerModal);
